import { useEffect, useState } from 'react';
import {
  atom,
  RecoilNodeInternal,
  RecoilRootInternal,
  RecoilValue,
  useRecoilRefresher_UNSTABLE,
  useRecoilValue,
  waitForNone,
} from 'recoil';

interface RecoilQueryOptions {
  cachePolicy?: 'no-cache' | 'default';
  if?: () => boolean;
}

const fakeLoadable = atom({
  key: 'useRecoilQuery/fakeLoadable',
  default: undefined,
});

export const useRecoilQuery = <T>(
  selectorMethod: RecoilValue<T>,
  options: RecoilQueryOptions = {
    cachePolicy: 'default',
  }
): { loading: boolean; error: Error | undefined; refresher: () => void; data: T | undefined } => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  const [data, setData] = useState<T | undefined>(undefined);
  const refresher = useRecoilRefresher_UNSTABLE(selectorMethod);
  const isQueryable = options.if ? options.if() : true;

  const [query] = useRecoilValue(waitForNone([isQueryable ? selectorMethod : fakeLoadable]));

  // FIXME: disable caching at utility function level since selector cache api is not stable
  // will probably need to revisit this once the recoil api is stable
  const storeRef = RecoilRootInternal.useStoreRef();

  useEffect(() => {
    if (options.cachePolicy === 'no-cache') {
      const store = storeRef.current;
      const { currentTree } = store.getState();
      const internalNode = RecoilNodeInternal.getNode(selectorMethod.key);
      internalNode.clearCache?.(store, currentTree);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (query.state === 'loading') {
      setLoading(true);
      setError(undefined);
    } else if (query.state === 'hasError') {
      setLoading(false);
      setError(query.contents);
    } else if (query.state === 'hasValue') {
      setLoading(false);
      setError(undefined);
      setData(query.contents);
    }
  }, [query]);

  return {
    loading,
    error,
    refresher,
    data: data as T,
  };
};
